import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Building2, ArrowLeft, Calculator, TrendingUp, DollarSign,
  AlertTriangle, CheckCircle, Hammer, FileText
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import { FREE_REPORT_GATE_MESSAGE, isFreeTier, normalizeTier } from "@/utils/planMessaging";
import { useReportQuota } from "@/hooks/useReportQuota";

const FlipCalculator = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const userTier = normalizeTier(user?.tier);
  const isFreeInvestor = isFreeTier(userTier);
  const {
    reportsUsedThisMonth,
    isStarter,
    isProLike,
    starterLimitReached,
    reportPlanText,
  } = useReportQuota(Boolean(user && token));
  
  const [formData, setFormData] = useState({
    property_name: "",
    address: "",
    purchase_price: 200000,
    closing_costs_buy: 4000,
    rehab_budget: 50000,
    rehab_duration_months: 3,
    holding_costs_monthly: 1500,
    financing_type: "cash",
    loan_amount: 0,
    interest_rate: 12,
    points: 2,
    after_repair_value: 325000,
    closing_costs_sell_percent: 8,
    contingency_percent: 10
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setAnalysis(null);
  };

  const calculateAnalysis = async () => {
    if (!formData.property_name) {
      toast.error("Please enter a property name");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/analysis/flip`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysis(response.data);
      toast.success("Analysis calculated and saved!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to calculate analysis");
    } finally {
      setSaving(false);
    }
  };

  const handleReportClick = () => {
    if (isFreeInvestor) {
      toast.error(FREE_REPORT_GATE_MESSAGE);
      return;
    }
    if (starterLimitReached) {
      toast.error("Monthly report limit reached. Upgrade to Pro for unlimited reports.");
      return;
    }
    navigate(`/report/flip/${analysis.id}`);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="flip-calculator-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">House Flipping Calculator</h1>
            </div>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              {analysis && (
                <Button
                  variant="outline"
                  onClick={handleReportClick}
                  disabled={starterLimitReached}
                  className="border-slate-700 text-slate-300 hover:text-white"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Report
                </Button>
              )}
              {analysis && (
                <div className="hidden md:flex flex-col leading-tight">
                  <span className="text-xs text-slate-300">{reportPlanText}</span>
                  {isStarter && (
                    <span className="text-xs text-slate-500">{reportsUsedThisMonth} of 5 reports used this month</span>
                  )}
                  {isProLike && (
                    <span className="text-xs text-slate-500">Unlimited report generation</span>
                  )}
                </div>
              )}
              {analysis && starterLimitReached && (
                <Button
                  size="sm"
                  onClick={() => navigate('/upgrade')}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                >
                  Upgrade to Pro
                </Button>
              )}
              <Button 
                onClick={calculateAnalysis}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                data-testid="calculate-flip-btn"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-slate-900 mr-2"></div>
                ) : (
                  <Calculator className="h-4 w-4 mr-2" />
                )}
                Calculate
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="space-y-6">
            {/* Property Details */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-amber-500" />
                Property Details
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Property Name *</Label>
                  <Input
                    value={formData.property_name}
                    onChange={(e) => handleInputChange('property_name', e.target.value)}
                    placeholder="123 Flip Street"
                    className="bg-slate-950 border-slate-800 text-white"
                    data-testid="flip-property-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Purchase Price</Label>
                    <Input
                      type="number"
                      value={formData.purchase_price}
                      onChange={(e) => handleInputChange('purchase_price', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                      data-testid="flip-purchase-price-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Closing Costs (Buy)</Label>
                    <Input
                      type="number"
                      value={formData.closing_costs_buy}
                      onChange={(e) => handleInputChange('closing_costs_buy', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Rehab Details */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Hammer className="h-5 w-5 text-cyan-500" />
                Rehab Details
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Rehab Budget</Label>
                    <Input
                      type="number"
                      value={formData.rehab_budget}
                      onChange={(e) => handleInputChange('rehab_budget', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                      data-testid="rehab-budget-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Duration (Months)</Label>
                    <Input
                      type="number"
                      value={formData.rehab_duration_months}
                      onChange={(e) => handleInputChange('rehab_duration_months', parseInt(e.target.value) || 1)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Holding Costs/Month</Label>
                    <Input
                      type="number"
                      value={formData.holding_costs_monthly}
                      onChange={(e) => handleInputChange('holding_costs_monthly', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Contingency (%)</Label>
                    <Input
                      type="number"
                      value={formData.contingency_percent}
                      onChange={(e) => handleInputChange('contingency_percent', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Financing */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-500" />
                Financing
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Financing Type</Label>
                  <Select 
                    value={formData.financing_type} 
                    onValueChange={(v) => handleInputChange('financing_type', v)}
                  >
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="hard_money">Hard Money</SelectItem>
                      <SelectItem value="conventional">Conventional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.financing_type !== 'cash' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Loan Amount</Label>
                        <Input
                          type="number"
                          value={formData.loan_amount}
                          onChange={(e) => handleInputChange('loan_amount', parseFloat(e.target.value) || 0)}
                          className="bg-slate-950 border-slate-800 text-white font-mono"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Interest Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={formData.interest_rate}
                          onChange={(e) => handleInputChange('interest_rate', parseFloat(e.target.value) || 0)}
                          className="bg-slate-950 border-slate-800 text-white font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300">Points (%)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.points}
                        onChange={(e) => handleInputChange('points', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Sale Details */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-purple-500" />
                Sale Details
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">After Repair Value (ARV)</Label>
                  <Input
                    type="number"
                    value={formData.after_repair_value}
                    onChange={(e) => handleInputChange('after_repair_value', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                    data-testid="arv-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Selling Costs (%)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={formData.closing_costs_sell_percent}
                    onChange={(e) => handleInputChange('closing_costs_sell_percent', parseFloat(e.target.value) || 0)}
                    className="bg-slate-950 border-slate-800 text-white font-mono"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {!analysis ? (
              <div className="glass-card rounded-xl p-12 text-center h-full flex flex-col items-center justify-center">
                <Hammer className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Enter Flip Details</h3>
                <p className="text-slate-400 mb-6">Fill in the form and click Calculate to see your flip analysis</p>
                <Button 
                  onClick={calculateAnalysis}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculate Now
                </Button>
              </div>
            ) : (
              <>
                {/* Profit Summary */}
                <div className={`rounded-xl p-6 ${analysis.results.gross_profit >= 0 ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg text-slate-300">Estimated Profit</span>
                    {analysis.results.gross_profit >= 0 ? (
                      <CheckCircle className="h-6 w-6 text-emerald-500" />
                    ) : (
                      <AlertTriangle className="h-6 w-6 text-red-500" />
                    )}
                  </div>
                  <div className={`text-4xl font-bold font-mono ${analysis.results.gross_profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`} data-testid="flip-profit">
                    {formatCurrency(analysis.results.gross_profit)}
                  </div>
                  <div className="mt-2 text-slate-400">
                    ROI: <span className={`font-mono ${analysis.results.roi >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatPercent(analysis.results.roi)}</span>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Cash Needed</div>
                    <div className="text-2xl font-bold font-mono text-white">
                      {formatCurrency(analysis.results.cash_needed)}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Cash ROI</div>
                    <div className={`text-2xl font-bold font-mono ${analysis.results.cash_roi >= 0 ? 'text-amber-500' : 'text-red-500'}`}>
                      {formatPercent(analysis.results.cash_roi)}
                    </div>
                  </div>
                </div>

                {/* 70% Rule Check */}
                <div className={`glass-card rounded-xl p-6 ${formData.purchase_price <= analysis.results.max_offer_70_rule ? 'border-emerald-500/30' : 'border-amber-500/30'}`}>
                  <h3 className="text-lg font-semibold text-white mb-4">70% Rule Check</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-400">Max Offer (70% Rule)</div>
                      <div className="text-2xl font-bold font-mono text-cyan-500">
                        {formatCurrency(analysis.results.max_offer_70_rule)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-slate-400">Your Offer</div>
                      <div className={`text-2xl font-bold font-mono ${formData.purchase_price <= analysis.results.max_offer_70_rule ? 'text-emerald-500' : 'text-amber-500'}`}>
                        {formatCurrency(formData.purchase_price)}
                      </div>
                    </div>
                  </div>
                  <div className={`mt-4 text-sm ${formData.purchase_price <= analysis.results.max_offer_70_rule ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {formData.purchase_price <= analysis.results.max_offer_70_rule 
                      ? '✓ Your offer is within the 70% rule guideline'
                      : '⚠ Your offer exceeds the 70% rule guideline'}
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Cost Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Purchase Price</span>
                      <span className="font-mono text-white">{formatCurrency(analysis.results.purchase_price)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Closing Costs (Buy)</span>
                      <span className="font-mono text-white">{formatCurrency(analysis.results.closing_costs_buy)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Rehab Budget</span>
                      <span className="font-mono text-white">{formatCurrency(analysis.results.rehab_budget)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Contingency ({formData.contingency_percent}%)</span>
                      <span className="font-mono text-white">{formatCurrency(analysis.results.contingency)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Holding Costs ({analysis.results.rehab_duration_months} mo)</span>
                      <span className="font-mono text-white">{formatCurrency(analysis.results.holding_costs)}</span>
                    </div>
                    {analysis.results.financing_costs > 0 && (
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Financing Costs</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.financing_costs)}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400 font-semibold">Total Costs</span>
                      <span className="font-mono text-amber-500 font-semibold">{formatCurrency(analysis.results.total_costs)}</span>
                    </div>
                  </div>
                </div>

                {/* Sale Analysis */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Sale Analysis</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">After Repair Value</span>
                      <span className="font-mono text-white">{formatCurrency(analysis.results.arv)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">Selling Costs ({formData.closing_costs_sell_percent}%)</span>
                      <span className="font-mono text-red-400">-{formatCurrency(analysis.results.closing_costs_sell)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400 font-semibold">Net Sale Price</span>
                      <span className="font-mono text-white font-semibold">{formatCurrency(analysis.results.net_sale_price)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default FlipCalculator;
